import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  connectorsQueryOptions,
  connectorLabel,
  sessionConnectorsQueryOptions,
  setSessionConnectors,
  type Connector,
  type SessionConnectorItem,
} from "../queries/connectors-queries";
import {
  agentsCatalogueQueryOptions,
  type Agent,
} from "../queries/agents-queries";
import { ConnectorIcon } from "@/shared/ui/ConnectorIcon";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import { sessionsQueryOptions } from "../queries/session-queries";

interface Props {
  /** Active session id; null on the hero. */
  sessionId: string | null;
  /** Hero-time agent override; null = default agent. Used only when sessionId is null. */
  pendingAgentRef: string | null;
}

const MAX_AVATARS = 3;

export function HeroConnectorsPill({ sessionId, pendingAgentRef }: Props) {
  const navigate = useNavigate();

  // ── Catalogue (current scope only) ──────────────────────────────
  // `scope=space` uses the active X-Space-Id header (or the caller's
  // personal space when absent), so a personal chat sees only personal
  // connectors and a team space sees only its own pinned ones.
  const { data: connectors = [], isLoading: connectorsLoading } = useQuery(
    connectorsQueryOptions("space"),
  );
  const catalogue = useMemo(() => {
    const m = new Map<string, Connector>();
    for (const c of connectors) m.set(c.id, c);
    return m;
  }, [connectors]);
  const enabledCatalogue = useMemo(
    () => connectors.filter((c) => c.enabled),
    [connectors],
  );

  // ── Hero source (no session yet) ────────────────────────────────
  const { data: agentsData } = useQuery(agentsCatalogueQueryOptions());
  const heroAgent = useMemo<Agent | null>(() => {
    if (sessionId) return null;
    const list = agentsData?.agents ?? [];
    const def = agentsData?.defaultAgent ?? null;
    if (pendingAgentRef) {
      return list.find((a) => a.ref === pendingAgentRef) ?? def;
    }
    return def;
  }, [sessionId, agentsData, pendingAgentRef]);

  const pendingConnectorIds = useAgentDraftStore((s) => s.pendingConnectorIds);
  const setPendingConnectorIds = useAgentDraftStore((s) => s.setPendingConnectorIds);

  // ── Session source (running chat) ───────────────────────────────
  const { data: sessionConnectors } = useQuery(
    sessionConnectorsQueryOptions(sessionId),
  );
  const queryClient = useQueryClient();
  const updateSession = useMutation({
    mutationFn: (ids: string[]) =>
      sessionId ? setSessionConnectors(sessionId, ids) : Promise.resolve(),
    onSuccess: () => {
      // Re-fetch the live status (active/failed) plus the session row so any
      // future "snapshot.connector_ids" reader sees fresh values.
      if (!sessionId) return;
      queryClient.invalidateQueries({ queryKey: ["sessions", sessionId, "connectors"] });
      queryClient.invalidateQueries({ queryKey: sessionsQueryOptions().queryKey });
    },
  });

  // ── Effective active set (hero override or session state) ───────
  const effectiveIds: string[] = useMemo(() => {
    if (sessionId) {
      return (sessionConnectors?.connectors ?? []).map((c) => c.id);
    }
    if (pendingConnectorIds !== null) return pendingConnectorIds;
    // Hero defaults: agent's explicit connector_ids only. Mirrors the
    // backend's no-override branch — space-pinned connectors stay in the
    // popover as available toggles, but are not auto-attached.
    return heroAgent?.connector_ids ?? [];
  }, [sessionId, sessionConnectors, pendingConnectorIds, heroAgent]);

  // Filter out ids that no longer exist in catalogue (deleted at source).
  const liveActiveIds = useMemo(
    () => effectiveIds.filter((id) => catalogue.has(id)),
    [effectiveIds, catalogue],
  );

  const failedCount = useMemo(() => {
    if (!sessionId) return 0;
    return (sessionConnectors?.connectors ?? []).filter((c) => c.status === "failed").length;
  }, [sessionId, sessionConnectors]);

  const itemsForRender: { id: string; label: string; url: string; status: SessionConnectorItem["status"]; error: string | null }[] = useMemo(() => {
    if (sessionId && sessionConnectors) {
      // Live items — already labeled by status from the server. Skip
      // "disabled" rows: those are zombie ids the snapshot still
      // references but whose source connector is gone or off — they
      // can't be toggled and aren't shown in the popover, so they
      // shouldn't add stray letter-avatars to the stack either.
      return sessionConnectors.connectors
        .filter((c) => c.status !== "disabled")
        .map((c) => ({
          id: c.id,
          label: c.label || c.name || "Коннектор",
          url: c.url,
          status: c.status,
          error: c.error,
        }));
    }
    // Hero — synthesise from catalogue. Status is "active" optimistically.
    return liveActiveIds.map((id) => {
      const c = catalogue.get(id);
      return {
        id,
        label: c ? connectorLabel(c) : "Коннектор",
        url: (c?.config?.url as string | undefined) ?? "",
        status: "active" as const,
        error: null,
      };
    });
  }, [sessionId, sessionConnectors, liveActiveIds, catalogue]);

  // ── Empty state on hero with no connectors in space at all ──────
  const showAddCta = !sessionId && enabledCatalogue.length === 0 && !connectorsLoading;
  const hideEntirely = sessionId !== null && enabledCatalogue.length === 0;

  // ── Popover state ───────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Open above the trigger when there isn't enough room below (e.g. in
  // active chat the InputBar sits at the bottom of the viewport). We use
  // the popover's actual height when it's mounted; on first paint we
  // optimistically place it down and re-measure once the ref is attached.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const trig = triggerRef.current?.getBoundingClientRect();
      if (!trig) return;
      const popH = popoverRef.current?.getBoundingClientRect().height ?? 360;
      const margin = 8;
      const spaceBelow = window.innerHeight - trig.bottom;
      const spaceAbove = trig.top;
      const flipUp = spaceBelow < popH + margin && spaceAbove > spaceBelow;
      const top = flipUp
        ? Math.max(margin, trig.top - popH - margin)
        : trig.bottom + margin;
      // Clamp horizontally so the popover never escapes the viewport.
      const popW = popoverRef.current?.getBoundingClientRect().width ?? 320;
      const left = Math.min(
        Math.max(margin, trig.left),
        window.innerWidth - popW - margin,
      );
      setPos({ top, left });
    };
    update();
    // Re-run once the popover has actually rendered so we have its real
    // height and can flip if needed.
    const raf = requestAnimationFrame(update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // ── Toggle ──────────────────────────────────────────────────────
  const toggle = (id: string) => {
    const next = liveActiveIds.includes(id)
      ? liveActiveIds.filter((x) => x !== id)
      : [...liveActiveIds, id];
    if (sessionId) {
      updateSession.mutate(next);
    } else {
      setPendingConnectorIds(next);
    }
  };

  if (hideEntirely) return null;

  if (showAddCta) {
    return (
      <div className="hero-foot-connectors">
        <button
          type="button"
          className="hero-conn-pill hero-conn-pill--cta"
          onClick={() => navigate({ to: "/connectors" })}
          title="Перейти к настройке коннекторов"
        >
          <span>+ Добавить коннектор</span>
        </button>
      </div>
    );
  }

  // Label rules:
  //   - 0 connectors:  "Подключить коннектор" (no icon stack)
  //   - 1..3:          up to 3 icons, no count text
  //   - 4+:            first 3 icons + "+ N коннекторов" (N = extras beyond 3)
  // Failures are appended as " · N ошибок" when present.
  const total = itemsForRender.length;
  const showIcons = total > 0;
  const extras = Math.max(0, total - MAX_AVATARS);
  const countText = (() => {
    if (total === 0) return "Подключить коннектор";
    if (extras > 0) return `+ ${extras} коннектор${plural(extras)}`;
    return "";
  })();
  const errorText =
    failedCount > 0 ? ` · ${failedCount} ошибк${pluralErr(failedCount)}` : "";

  return (
    <>
      <div className="hero-foot-connectors" ref={wrapRef}>
        <button
          ref={triggerRef}
          type="button"
          className={`hero-conn-pill${failedCount > 0 ? " hero-conn-pill--has-error" : ""}`}
          onClick={() => setOpen((v) => !v)}
        >
          {showIcons && (
            <span className="hero-conn-stack" aria-hidden="true">
              {itemsForRender.slice(0, MAX_AVATARS).map((item) => (
                <span
                  key={item.id}
                  className={`hero-conn-avatar-wrap${item.status === "failed" ? " hero-conn-avatar-wrap--failed" : ""}`}
                  title={item.status === "failed" ? `${item.label} — ошибка: ${item.error ?? "не удалось подключиться"}` : item.label}
                >
                  <ConnectorIcon url={item.url || null} label={item.label} size={24} />
                </span>
              ))}
            </span>
          )}
          {(countText || errorText) && (
            <span>
              {countText}
              {errorText}
            </span>
          )}
          <svg viewBox="0 0 20 20" aria-hidden="true" fill="none">
            <path d="M6.5 8.5 10 12l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {open && pos && createPortal(
        <div
          ref={popoverRef}
          className="hero-conn-popover"
          role="menu"
          style={{ top: pos.top, left: pos.left }}
        >
          {enabledCatalogue.length === 0 ? (
            <div className="hero-conn-popover-empty">
              В пространстве пока нет коннекторов.
            </div>
          ) : (
            <ul className="hero-conn-popover-list">
              {enabledCatalogue.map((c) => {
                const liveItem = sessionId
                  ? sessionConnectors?.connectors.find((x) => x.id === c.id)
                  : null;
                const isOn = liveActiveIds.includes(c.id);
                const failed = liveItem?.status === "failed";
                return (
                  <li key={c.id}>
                    <label
                      className={`hero-conn-popover-item${isOn ? " is-active" : ""}${failed ? " is-failed" : ""}`}
                      title={
                        failed
                          ? `Ошибка: ${liveItem?.error ?? "не удалось подключиться"}`
                          : c.description || undefined
                      }
                    >
                      <span className="hero-conn-popover-icon">
                        <ConnectorIcon
                          url={(c.config?.url as string | undefined) ?? null}
                          label={connectorLabel(c)}
                          size={20}
                        />
                      </span>
                      <span className="hero-conn-popover-name">{connectorLabel(c)}</span>
                      <span
                        className={`hero-conn-toggle${isOn ? " is-on" : ""}`}
                        aria-hidden="true"
                      >
                        <span className="hero-conn-toggle-knob" />
                      </span>
                      <input
                        type="checkbox"
                        className="hero-conn-toggle-input"
                        checked={isOn}
                        onChange={() => toggle(c.id)}
                      />
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            className="hero-conn-popover-more"
            onClick={() => {
              setOpen(false);
              navigate({ to: "/connectors" });
            }}
          >
            <span>Управление коннекторами</span>
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

// Russian pluralisation for "коннектор": 1 коннектор, 2-4 коннектора, 5+ коннекторов.
function plural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "а";
  return "ов";
}

// Russian pluralisation for "ошибка": 1 ошибка, 2-4 ошибки, 5+ ошибок.
function pluralErr(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "а";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "и";
  return "";
}
